import { Badge, Tag, Timeline, Typography } from 'antd';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import tw from 'twin.macro';

import { NoticeIcon } from '@/assets/svg';
import Filter from '@/components/Filter';
import NoticeBox from '@/components/NoticeBox';
import TooltipText from '@/components/TooltipText';
import Table from '@/components/list/Table';
import { CONTRACT_STATUS, CUSTOMER_TYPE, UNIT_TYPE } from '@/constants';
import { useContracts, useMyPermissions } from '@/hooks/queries/universal';
import usePagination from '@/hooks/usePagination';
import { selectedBranchIdAtom } from '@/store/atoms';
import type { GetContractsParams } from '@/types/universal';

const ContractIndex = () => {
  const selectedBranchId = useAtomValue(selectedBranchIdAtom);
  const pagination = usePagination<Omit<GetContractsParams, 'branchId'>>([
    'contractStatus',
    'clientType',
    'contractStartAtAfter',
    'contractStartAtBefore',
    'contractEndAtAfter',
    'contractEndAtBefore',
  ]);
  const query = useContracts(
    { branchId: selectedBranchId as number, ...pagination.query },
    { enabled: !!selectedBranchId }
  );
  const { data: myPermissions } = useMyPermissions();
  const isAdmin = myPermissions?.role === 'ADMIN';

  return (
    <div css={tw`flex flex-col gap-[32px]`}>
      <NoticeBox icon={<NoticeIcon />}>
        할인율은 정가 기준에서 적용된 할인율입니다. 다이나믹 프라이싱, 프로모션
        적용에 따라 할인율이 달라집니다. 프로모션은 쿠폰 적용 여부를 표시합니다.
      </NoticeBox>

      <Filter
        pagination={pagination}
        filters={[
          {
            label: '계약 상태',
            name: 'contractStatus',
            type: 'select',
            placeholder: '전체',
            options: CONTRACT_STATUS,
          },
          {
            label: '회원 유형',
            name: 'clientType',
            type: 'select',
            placeholder: '전체',
            options: CUSTOMER_TYPE,
          },
          {
            label: '계약 시작일',
            names: ['contractStartAtAfter', 'contractStartAtBefore'],
            type: 'dateRange',
            placeholder: '전체',
          },
          {
            label: '계약 종료일',
            names: ['contractEndAtAfter', 'contractEndAtBefore'],
            placeholder: '전체',
            type: 'dateRange',
          },
        ]}
      />

      <Table
        headerConfig={{
          showTotalCount: true,
        }}
        columns={[
          {
            dataIndex: 'id',
            title: 'No',
            fixed: 'left',
            render: (value) =>
              isAdmin ? (
                <Typography.Link
                  href={`${process.env.REACT_APP_ADMIN_DOMAIN}/contract/view?id=${value}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {value}
                </Typography.Link>
              ) : (
                value
              ),
          },
          {
            dataIndex: 'unitType',
            title: '유닛 타입',
            fixed: 'left',
            render: (value) => UNIT_TYPE[value as keyof typeof UNIT_TYPE],
          },
          { dataIndex: 'unitName', title: '유닛 번호', fixed: 'left' },
          {
            dataIndex: 'contractStatus',
            title: '계약 상태',
            render: (value, record) => (
              <Badge
                status={(() => {
                  switch (record.contractStatus) {
                    case 'RESERVED':
                      return 'success';
                    case 'NOT_PAID':
                    case 'CANCELED':
                      return 'error';
                    case 'CONTRACT_END':
                      return 'warning';
                    default:
                      return 'default';
                  }
                })()}
                text={CONTRACT_STATUS[value as keyof typeof CONTRACT_STATUS]}
              />
            ),
          },
          {
            dataIndex: 'period',
            title: (
              <TooltipText
                description={
                  '계약 시작일: 사용자가 계약을 체결한 날짜\n이용 시작일: 유닛을 사용하기로 한 날짜'
                }
              >
                계약 / 이용일
              </TooltipText>
            ),
            render: (_, record) => {
              const timelineItems = [
                record.contractStartAt && {
                  children: (
                    <>
                      <Typography.Text type="secondary">
                        계약 시작일
                      </Typography.Text>
                      {dayjs(record.contractStartAt).format('YYYY-MM-DD')}
                    </>
                  ),
                },
                record.usageStartAt && {
                  children: (
                    <>
                      <Typography.Text type="secondary">
                        이용 시작일
                      </Typography.Text>
                      {dayjs(record.usageStartAt).format('YYYY-MM-DD')}
                    </>
                  ),
                },
                record.contractEndAt && {
                  children: (
                    <>
                      <Typography.Text type="secondary">
                        계약 종료일
                      </Typography.Text>
                      {dayjs(record.contractEndAt).format('YYYY-MM-DD')}
                    </>
                  ),
                },
                record.usageEndAt && {
                  children: (
                    <>
                      <Typography.Text type="secondary">
                        이용 종료일
                      </Typography.Text>
                      {dayjs(record.usageEndAt).format('YYYY-MM-DD')}
                    </>
                  ),
                },
              ].filter((item) => !!item);

              return <Timeline items={timelineItems} />;
            },
          },
          {
            dataIndex: 'clientType',
            title: '회원 유형',
            align: 'center',
            render: (value) => (
              <Tag>{CUSTOMER_TYPE[value as keyof typeof CUSTOMER_TYPE]}</Tag>
            ),
          },
          {
            dataIndex: 'originPrice',
            title: '정가',
            align: 'right',
            render: (_, row) => row.originPrice?.toLocaleString(),
          },
          {
            dataIndex: 'discountedRate',
            title: '할인율',
            align: 'right',
            render: (_, row) => `${row.discountedRate}%`,
          },
          {
            dataIndex: 'discountedPrice',
            title: '판매가',
            align: 'right',
            render: (_, row) => row.discountedPrice?.toLocaleString(),
          },
          {
            dataIndex: 'isUsedPromotion',
            title: '프로모션',
            align: 'center',
            render: (value) => <Tag>{value ? '적용' : '미적용'}</Tag>,
          },
        ]}
        dataSource={query.data?.results}
        rowKey={(row) => row.id}
        pagination={{ total: query.data?.count, ...pagination }}
      />
    </div>
  );
};

export default ContractIndex;
